import { useRef, useCallback, useEffect } from 'react';

/**
 * Mobile-friendly numeric stepper for SPH / CYL / Axis values.
 * Props:
 *   value      {number|null}
 *   onChange   {(number) => void}
 *   min        {number}
 *   max        {number}
 *   step       {number}
 *   label      {string}
 *   formatter  {(number) => string}  optional display formatter
 *   disabled   {boolean}
 */
export default function PowerStepper({
  value,
  onChange,
  min,
  max,
  step = 0.25,
  label,
  formatter,
  disabled = false,
}) {
  const pressTimerRef = useRef(null);
  const intervalRef   = useRef(null);

  // Keep refs in sync so long-press callbacks always see latest value/onChange
  const valueRef    = useRef(value);
  const onChangeRef = useRef(onChange);
  useEffect(() => { valueRef.current = value; },    [value]);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const fmt = formatter || defaultFmt;

  const clamp = useCallback(
    (v) => Math.round(Math.min(max, Math.max(min, v)) / step) * step,
    [min, max, step]
  );

  const increment = useCallback(() => {
    onChangeRef.current(clamp((valueRef.current ?? 0) + step));
  }, [clamp, step]);

  const decrement = useCallback(() => {
    onChangeRef.current(clamp((valueRef.current ?? 0) - step));
  }, [clamp, step]);

  const startRepeat = useCallback((fn) => {
    fn();
    pressTimerRef.current = setTimeout(() => {
      intervalRef.current = setInterval(fn, 100);
    }, 400);
  }, []);

  const stopRepeat = useCallback(() => {
    clearTimeout(pressTimerRef.current);
    clearInterval(intervalRef.current);
  }, []);

  // Clean up on unmount
  useEffect(() => stopRepeat, [stopRepeat]);

  return (
    <div className="flex flex-col items-center gap-1">
      {label && (
        <span className="text-xs font-semibold text-clay uppercase tracking-wide">
          {label}
        </span>
      )}
      <div className={`flex items-center rounded-xl border ${disabled ? 'border-sand bg-cream' : 'border-sand bg-white'} overflow-hidden shadow-sm`}>
        {/* Decrement button */}
        <button
          type="button"
          disabled={disabled || value === min}
          onMouseDown={() => startRepeat(decrement)}
          onMouseUp={stopRepeat}
          onMouseLeave={stopRepeat}
          onTouchStart={(e) => { e.preventDefault(); startRepeat(decrement); }}
          onTouchEnd={stopRepeat}
          onTouchCancel={stopRepeat}
          className="w-11 h-11 flex items-center justify-center text-xl font-bold text-clay
                     hover:bg-brand-50 hover:text-brand-600 active:bg-brand-100 disabled:opacity-30 disabled:cursor-not-allowed
                     transition-colors select-none touch-manipulation"
          aria-label="Decrease"
        >
          −
        </button>

        {/* Value display */}
        <div
          className={`w-20 h-11 flex items-center justify-center font-mono text-sm font-semibold
                      ${value === null ? 'text-clay italic' : 'text-ink'}
                      border-x border-sand`}
        >
          {value === null ? '—' : fmt(value)}
        </div>

        {/* Increment button */}
        <button
          type="button"
          disabled={disabled || value === max}
          onMouseDown={() => startRepeat(increment)}
          onMouseUp={stopRepeat}
          onMouseLeave={stopRepeat}
          onTouchStart={(e) => { e.preventDefault(); startRepeat(increment); }}
          onTouchEnd={stopRepeat}
          onTouchCancel={stopRepeat}
          className="w-11 h-11 flex items-center justify-center text-xl font-bold text-clay
                     hover:bg-brand-50 hover:text-brand-600 active:bg-brand-100 disabled:opacity-30 disabled:cursor-not-allowed
                     transition-colors select-none touch-manipulation"
          aria-label="Increase"
        >
          +
        </button>
      </div>
    </div>
  );
}

// Default formatter: show sign and 2 decimal places; 0.00 shown as "Plano"
function defaultFmt(n) {
  if (n === 0) return 'Plano';
  const abs = Math.abs(n).toFixed(2);
  return n > 0 ? `+${abs}` : `-${abs}`;
}
